import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subject} from 'rxjs';
import {NotificationService} from '../../../notification/notification.service';
import {takeUntil} from 'rxjs/operators';
import {SubscriptionUtils} from '../../../commons/subscription.utils';
import {BoschParkingInfo, BoschParkingPricing} from './model/bosch-parking.info';
import {BoschAccessInfo, BoschBookingControls, BoschBookingInfo, NewBookingInfo} from './model/bosch-booking.info';
import {BoschBookingForm} from './model/bosch-booking.form';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {validateTillLaterThanFrom} from '../../../validators';
import {VehicleDriverInfo} from '../model/vehicle-driver.info';
import {ParkingService} from '../parking.service';
import {ParkingBookingStatus} from '../../../../pages/client/poi/parking-booking/parking-booking.component';

let moment = require('moment');

@Component({
  selector: 'app-bosch-parking-form',
  templateUrl: './bosch-parking-form.component.html',
  styleUrls: ['./bosch-parking-form.component.scss']
})
export class BoschParkingFormComponent implements OnInit, OnDestroy {

  @Input()
  public modalReference: any;

  @Input()
  public vehicleId: number;

  @Input()
  public parkingId: string;

  @Input()
  public bookingId: number;

  @Input()
  public taskId: number;

  @Input()
  public etaToParking: string;

  @Output()
  public newBooking = new EventEmitter<NewBookingInfo>();

  @Output()
  public bookingStatus = new EventEmitter<ParkingBookingStatus>();

  public minDate: NgbDateStruct;
  public parkingAvailable = false;
  public previousStart: string;
  public previousEnd: string;
  public parkingInfo: BoschParkingInfo;
  public parkingPricing: BoschParkingPricing;
  public bookingInfo: BoschBookingInfo;
  public bookingForm: FormGroup<BoschBookingControls>;
  public hasCredentials = true;
  public defaultHours = [8, 10, 12, 24];
  public vehicleOwnerTitle: string;

  private vehicleDriver: VehicleDriverInfo;
  private subSubject = new Subject<boolean>();

  constructor(private fb: FormBuilder,
              private parkingService: ParkingService,
              private notification: NotificationService) {
  }

  public ngOnInit(): void {
    this.setMinDate();
    if (this.bookingId != null) {
      this.parkingService.fetchBoschBookingInfo(this.bookingId, this.vehicleId)
        .pipe(takeUntil(this.subSubject)).subscribe({
        next: (data) => {
          if (data != null) {
            this.bookingInfo = data;
            this.bookingStatus.emit(data.state);
          } else {
            this.handleNoCredentials();
          }
        },
        error: () => this.notification.loadingError()
      });
    } else {
      this.parkingService.fetchBoschParkingInfo(this.parkingId, this.vehicleId)
        .pipe(takeUntil(this.subSubject)).subscribe({
        next: (data) => {
          if (data != null) {
            this.parkingInfo = data;
            this.parkingService.fetchVehicleDriver(this.vehicleId).pipe(takeUntil(this.subSubject)).subscribe({
              next: (data) => {
                this.vehicleDriver = data;
                this.buildForm(data);
              },
              error: () => this.notification.loadingError()
            })
          } else {
            this.handleNoCredentials();
          }
        },
        error: () => this.notification.loadingError()
      });
    }
  }

  public closeModal(): void {
    this.modalReference.close();
    this.modalReference = null;
  }

  public bookParking(): void {
    let form = this.toBookingForm();
    this.parkingService.bookBoschParking(form, this.vehicleId).pipe(takeUntil(this.subSubject)).subscribe({
      next: (data) => {
        if (data != null) {
          this.notification.savingSuccess();
          this.bookingInfo = data;
          this.bookingId = data.id;
          this.newBooking.emit({
            bookingId: data.id,
            driverLang: this.vehicleDriver.driverLang,
            reservationLink: this.resolveReservationLink(data.accessInfo),
            startDate: form.startDate
          });
        } else {
          this.handleNoCredentials();
        }
      },
      error: () => this.notification.storingError()
    });
  }

  private resolveReservationLink(accessInfo: BoschAccessInfo[]): string {
    let info = accessInfo.find(it => it.accessTypeKey === 'LINK');
    return info != null
      ? info.value
      : null;
  }

  public checkPricing(): void {
    let startDate = this.bookingForm.value.startDate;
    let endDate = this.bookingForm.value.endDate;
    if (startDate?.length > 0 && endDate?.length > 0) {
      this.parkingService.fetchBoschParkingPricing(this.parkingId, startDate, endDate, this.vehicleId)
        .pipe(takeUntil(this.subSubject))
        .subscribe({
          next: (data) => this.handlePricing(data, startDate, endDate),
          error: () => this.notification.loadingError()
        })
    }
  }

  public cancelBooking(): void {
    this.parkingService.cancelBoschBooking(this.bookingId, this.vehicleId)
      .pipe(takeUntil(this.subSubject)).subscribe({
      next: (success) => {
        if (success == null) {
          this.handleNoCredentials();
        } else if (success) {
          this.bookingInfo.state = 'CANCELLED';
          this.newBooking.emit(null);
          this.closeModal();
          this.notification.deletingSuccess();
        } else {
          this.notification.deletingError();
        }
      },
      error: () => this.notification.deletingError()
    });
  }

  public setEnd(hour: number): void {
    let startDate = this.bookingForm.value.startDate;
    if (startDate?.length > 0) {
      let endDate = moment(startDate, 'YYYY-MM-DD HH:mm').add(hour, 'hours').format('YYYY-MM-DD HH:mm');
      this.bookingForm.patchValue({
        endDate: endDate
      });
    }
  }

  private toBookingForm(): BoschBookingForm {
    return new BoschBookingForm(
      this.parkingId,
      this.bookingForm.value.startDate,
      this.bookingForm.value.endDate,
      this.bookingForm.value.firstName,
      this.bookingForm.value.lastName,
      this.vehicleDriver.driverLang,
      this.taskId,
      this.bookingForm.value.licensePlate,
      this.bookingForm.value.accessTypes
    );
  }

  private handlePricing(data: BoschParkingPricing, startDate: string, endDate: string): void {
    if (data != null) {
      this.parkingPricing = data;
      this.parkingAvailable = data.available;
      this.previousStart = startDate;
      this.previousEnd = endDate;
      let accessTypes = this.bookingForm.controls.accessTypes;
      accessTypes.clear();
      data.accessTypeKeys.forEach(accessKey => {
        if (accessKey != null) {
          accessTypes.push(new FormControl(accessKey));
        }
      });
    } else {
      this.handleNoCredentials();
    }
  }

  private buildForm(data: VehicleDriverInfo): void {
    if (this.etaToParking != null) {
      let eta = moment(this.etaToParking, 'YYYY-MM-DD HH:mm');
      let minutes = eta.minutes();
      this.etaToParking = eta.add(minutes < 30 ? 30 - minutes : 60 - minutes, 'minutes').format('YYYY-MM-DD HH:mm');
    }
    this.bookingForm = this.fb.group<BoschBookingControls>({
      startDate: new FormControl(this.etaToParking != null ? this.etaToParking : '', Validators.required),
      endDate: new FormControl('', Validators.required),
      firstName: new FormControl(data != null ? data.firstName : '', Validators.required),
      lastName: new FormControl(data != null ? data.lastName : ''),
      licensePlate: new FormControl(data != null ? data.licensePlate : null, Validators.required),
      accessTypes: new FormArray([])
    }, {
      validators: validateTillLaterThanFrom('startDate', 'endDate')
    })
  }

  private setMinDate(): void {
    let current = new Date();
    this.minDate = {
      year: current.getUTCFullYear(),
      month: current.getUTCMonth() + 1,
      day: current.getUTCDate()
    };
  }

  private handleNoCredentials(): void {
    this.hasCredentials = false;
    this.parkingService.fetchVehicleOwnerTitle(this.vehicleId)
      .pipe(takeUntil(this.subSubject))
      .subscribe({
        next: (title) => this.vehicleOwnerTitle = title,
        error: () => this.notification.loadingError()
      });
  }

  public ngOnDestroy(): void {
    SubscriptionUtils.safeUnsubscribeSubject(this.subSubject);
  }
}
